import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Stack,
  Text,
  VStack,
  Spacer,
  Box
} from '@chakra-ui/react';
import { ContentCard } from "../Components/ContentCard";
import StyledInput from '../Components/ui/styled-input';
import ErrorAlert from '../Components/ui/error-alert';
import { useSignUp } from './hooks/useSignUp';
import { LuEye, LuEyeOff } from 'react-icons/lu';

interface SignUpFormProps {
  successPath?: string;
  onSuccess?: () => void;
}

const PasswordField: React.FC<{
  id?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  required?: boolean;
}> = ({ id, value, onChange, placeholder, required }) => {
  const [show, setShow] = React.useState(false);
  const handleClick = () => setShow(!show);

  return (
    <InputGroup>
      <Input
        id={id}
        type={show ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        bg="white"
        borderWidth="1px"
        borderColor="gray.300"
        borderRadius="md"
        color="gray.900"
        height="48px"
        px="1rem"
        fontSize="md"
        _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
        _placeholder={{ color: "gray.400" }}
      />
      <InputRightElement h="48px">
        <IconButton
          size="sm"
          variant="ghost"
          aria-label={show ? 'Hide password' : 'Show password'}
          icon={show ? <LuEyeOff /> : <LuEye />}
          onClick={handleClick}
        />
      </InputRightElement>
    </InputGroup>
  );
};

export const SignupForm: React.FC<SignUpFormProps> = ({ successPath, onSuccess }) => {
  const [searchParams] = useSearchParams();
  const {
    name, setName, email, setEmail, password, setPassword,
    confirmPassword, setConfirmPassword, inviteToken, setInviteToken, coupon, setCoupon,
    isSubmitting, error, setError, handleSubmit
  } = useSignUp({ successPath, onSuccess });

  React.useEffect(() => {
    const couponParam = searchParams.get('coupon');
    if (couponParam && !coupon) {
      setCoupon(couponParam);
    }
  }, [searchParams, coupon, setCoupon]);

  return (
    <Container centerContent maxW="lg" py={{ base: '12', md: '24' }} px={{ base: '0', sm: '8' }}>
      <VStack spacing={8}>
        <VStack spacing={6}>
          <VStack spacing={{ base: 2, md: 3 }} textAlign="center">
            <Heading as="h1" size="2xl" fontWeight="bold" lineHeight="1.1" color="gray.900">Sign up for an account</Heading>
            <Text color="gray.600">
              Already have an account? <Link to="/signin" style={{ color: 'var(--chakra-colors-blue-600)' }}>Sign In</Link>
            </Text>
          </VStack>
        </VStack>

        <ContentCard>
          <Stack as="form" spacing={6} width="md" onSubmit={handleSubmit}>
            <Stack spacing={6}>
              <FormControl isRequired>
                <FormLabel htmlFor="signup-name">Name</FormLabel>
                <StyledInput
                  id="signup-name"
                  type="text"
                  value={name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel htmlFor="signup-email">Email</FormLabel>
                <StyledInput
                  id="signup-email"
                  type="email"
                  value={email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel htmlFor="signup-password">Password</FormLabel>
                <PasswordField
                  id="signup-password"
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel htmlFor="signup-confirm-password">Confirm Password</FormLabel>
                <PasswordField
                  id="signup-confirm-password"
                  value={confirmPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Coupon code</FormLabel>
                <StyledInput
                  type="text"
                  value={coupon}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCoupon(e.target.value)}
                  placeholder="Optional"
                />
              </FormControl>
              <Spacer />
              {/* Password strength indicator */}
              <Box width="100%" bg="gray.100" h="8px" borderRadius="full">
                <Box
                  width={`${Math.min(password.length * 10, 100)}%`}
                  bg={password.length < 4 ? "red.500" : password.length < 8 ? "yellow.500" : "green.500"}
                  h="100%"
                  borderRadius="full"
                  transition="width 0.3s ease-in-out"
                />
              </Box>
            </Stack>
            <Stack spacing={6}>
              <Button
                type="submit"
                colorScheme="blue"
                size="lg"
                width="full"
                fontWeight="semibold"
                borderRadius="md"
                isLoading={isSubmitting ? true : undefined}
              >
                Sign Up
              </Button>
            </Stack>
            <ErrorAlert
              status="error"
              message={error}
              onClose={() => setError(null)}
              mt={4}
            />
          </Stack>
        </ContentCard>
      </VStack>
    </Container>
  );
};
